import styles from './PageAssembly.scss';

import cx from 'classnames';
import React, { CSSProperties, useEffect, useMemo } from 'react';

import { ReferencedEntry } from 'contentful';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { ContentType, Page } from 'src/api/contentful/contentTypes.cloverdotcom';
import { useNavBar } from 'src/components/NavBarContext/UseNavBar';
import { HeroModule } from 'src/components/PageAssembly/HeroModule/HeroModule';
import { useSidebarOverride } from 'src/components/SidebarContext/UseSidebarOverride';
import { useScrollOnRender } from 'src/hooks/UseScrollOnRender';
import { targetView } from 'src/services/target';
import { NEW_REDIRECT_ROUTE } from 'src/utils/AppRoute';
import { getContentType } from 'src/utils/CloverdotcomEntryUtil';
import { componentKey, getField, isEntry } from 'src/utils/EntryUtil';
import { LocalStorage } from 'src/utils/LocalStorage';
import { isModalUrl } from 'src/utils/ModalRoute';
import { getParsedHtmlLangTagList } from 'src/utils/PageAssemblyUtil';
import { ensureNoTrailingSlash } from 'src/utils/Urls';
import { normalizeSlug } from '../App/Routes/PagesSwitch/PagesSwitch';
import { useSpeedbumpEntry } from '../App/Routes/UseSpeedbumpEntry';
import { applyEntryStyleAttributes } from '../EntryWrapper/EntryStyleWrapper';
import { NotFoundPage } from '../ErrorPage/NotFoundPage';
import { SomethingWentWrongPage } from '../ErrorPage/SomethingWentWrongPage';
import { useFeatureFlagsContext } from '../FeatureFlags/FeatureFlags';
import { useNavBarContext } from '../NavBarContext/NavBarContext';
import { NestedModals } from '../NestedRoute/NestedModals';
import { PageHeaderBanner } from '../PageHeaderBanner/PageHeaderBanner';
import { useScrollPositionContext } from '../ScrollPositionProvider/ScrollPositionContext';
import { Shopbump } from '../Shopbump/Shopbump';
import { SubNavBar } from '../SubNavigation/SubNavBar';
import { ModuleWrapper } from './ModuleWrapper/ModuleWrapper';
import { setDefaultReseller } from './Utils';
import { VerticalHero } from './VerticalHero/VerticalHero';

interface PageAssemblyProps extends React.HTMLProps<HTMLDivElement> {
  entry?: ReferencedEntry<Page>;
}

export const matchPathToSlug = (entry: ReferencedEntry<Page>) => {
  const slug = normalizeSlug(getField(entry, 'slug'));
  if (slug === 'shop' || isModalUrl(window.location.pathname)) {
    return true;
  }

  return normalizeSlug(window.location.pathname) === slug;
};

const PageAssembly: React.FC<PageAssemblyProps> = props => {
  const { entry } = props;

  if (!isEntry(entry)) return <SomethingWentWrongPage />;

  // All page types should have an 'items' field will an array of all dynamic modules
  const {
    backgroundColor,
    bannerHidden = false,
    header,
    headerUtil,
    hideCartIcon,
    heroTopic,
    items = [],
    sidebar,
    pageMetadata,
    verticalMarket,
    alternatingModuleColor,
    referralId,
    referralIdDev,
    subNavigationBar,
  } = entry.fields;

  const contentTypeId = getContentType(entry) as ContentType;

  if (ContentType.BASIC_PAGE !== contentTypeId) {
    console.warn(`Unknown page type provided to PageAssembly: '${contentTypeId}'`);
    return null;
  }

  if (referralId || referralIdDev) setDefaultReseller(referralId, referralIdDev);

  const { i18n } = useTranslation();
  const { speedbumpEntry } = useSpeedbumpEntry(i18n.language);
  const isInternationalRedirectOn = LocalStorage.tryGetItem(NEW_REDIRECT_ROUTE) === 'true';
  const isUSLocale = i18n.language === 'en-US';

  const setHasSubNavBar = useNavBarContext().setHasSubNavBar;
  useSidebarOverride(sidebar);
  useNavBar(headerUtil, header, hideCartIcon);

  const { isFlagEnabled } = useFeatureFlagsContext();
  const isSubNavEnabled = isFlagEnabled('use-sub-navigation');
  setHasSubNavBar(!!subNavigationBar);

  useScrollOnRender();
  const { previousScrollPosition, scrollPositionToRestore, setHasPageLoaded } = useScrollPositionContext();

  useEffect(() => {
    setHasPageLoaded(true);
    return () => {
      setHasPageLoaded(false);
      scrollPositionToRestore.current = previousScrollPosition.current;
      previousScrollPosition.current = window.scrollY;
    };
  }, []);

  useEffect(() => {
    targetView();
  }, [entry]);

  const modules = useMemo(() => {
    return items.map((item, index) => <ModuleWrapper key={componentKey(item, index)} entry={item} />);
  }, [items]);

  // new react route logic will check and take care 404 if path doesn't match to slug
  if (!isInternationalRedirectOn && !matchPathToSlug(entry)) {
    return <NotFoundPage />;
  }

  const canonicalTag = getField(entry, 'canonicalTag');
  const hrefLangTagList = getParsedHtmlLangTagList(getField(entry, 'hrefLangTagList'));

  return (
    <article
      className={cx(styles.pageAssembly, alternatingModuleColor && styles.pageAssemblyAlternatingModuleColor)}
      style={{ backgroundColor, '--alternating-module-color': alternatingModuleColor } as CSSProperties}
    >
      <Helmet>
        {pageMetadata?.fields?.title && <title>{pageMetadata.fields.title}</title>}
        {pageMetadata?.fields?.description && <meta name="description" content={pageMetadata.fields.description} />}
        {canonicalTag && <link rel="canonical" href={ensureNoTrailingSlash(canonicalTag)} />}
        {hrefLangTagList}
      </Helmet>
      {isSubNavEnabled && !!subNavigationBar && <SubNavBar />}
      {!bannerHidden && isUSLocale && <PageHeaderBanner />}
      {!verticalMarket
        ? <HeroModule heroTopic={heroTopic} isHomepage={getField(entry, 'slug') === '/'} />
        : <VerticalHero entry={heroTopic} />}
      {modules}
      {getField(entry, 'showSpeedbump', false) && <Shopbump entry={speedbumpEntry} />}
      <NestedModals />
    </article>
  );
};

const WrappedComponent = applyEntryStyleAttributes(PageAssembly);

export {
  WrappedComponent as PageAssembly
};
